import { z } from 'zod';
import { approvalStatusSchema, idSchema, isoDateTimeSchema, moneySchema } from './common';

/* ------------------------------------------------------------------ *
 * User
 * ------------------------------------------------------------------ */

export const kycStatusSchema = z.enum(['none', 'pending', 'approved', 'rejected']);
export type KycStatus = z.infer<typeof kycStatusSchema>;

export const userStatusSchema = z.enum(['active', 'inactive', 'blocked']);
export type UserStatus = z.infer<typeof userStatusSchema>;

export const bankDetailsSchema = z.object({
  account_holder: z.string().nullable(),
  account_number: z.string().nullable(),
  bank_name: z.string().nullable(),
  ifsc: z.string().nullable(),
  branch: z.string().nullable(),
});
export type BankDetails = z.infer<typeof bankDetailsSchema>;

export const nomineeSchema = z.object({
  name: z.string().nullable(),
  relation: z.string().nullable(),
  dob: z.string().nullable(),
  phone: z.string().nullable(),
});
export type Nominee = z.infer<typeof nomineeSchema>;

export const userSchema = z.object({
  id: idSchema,
  /** Public member code, e.g. AO123456. */
  user_code: z.string(),
  name: z.string(),
  // Nullable: a wallet/DApp-registered member (2026-07-30 — see
  // registerWallet()) has no email at all, only wallet_address.
  email: z.string().email().nullable(),
  phone: z.string().nullable(),
  sponsor_id: idSchema.nullable(),
  sponsor_code: z.string().nullable(),
  status: userStatusSchema,
  is_activated: z.boolean(),
  activated_at: isoDateTimeSchema.nullable(),
  kyc_status: kycStatusSchema,
  two_factor_enabled: z.boolean(),
  /** Unilevel depth relative to the requesting user, when listed under a team. */
  level: z.number().nullable().optional(),
  /** Binary placement leg, when listed under a binary tree query. */
  leg: z.enum(['left', 'right']).nullable().optional(),
  rank_code: z.string().nullable(),
  wallet_address: z.string().nullable(),
  created_at: isoDateTimeSchema,
});
export type User = z.infer<typeof userSchema>;

/** Shape returned by /auth/me — includes impersonation state (§6.2 banner). */
export const sessionSchema = z.object({
  user: userSchema,
  is_admin: z.boolean(),
  is_impersonating: z.boolean(),
  impersonator_name: z.string().nullable().optional(),
  permissions: z.array(z.string()).default([]),
});
export type Session = z.infer<typeof sessionSchema>;

/* ------------------------------------------------------------------ *
 * Package + Activation
 * ------------------------------------------------------------------ */

export const packageSchema = z.object({
  id: idSchema,
  name: z.string(),
  /** Alpha One joining package: $100 in LDS Token, one-time. */
  amount_usd: moneySchema,
  amount_lds: moneySchema.nullable(),
  description: z.string().nullable(),
  is_active: z.boolean(),
  created_at: isoDateTimeSchema,
});
export type Package = z.infer<typeof packageSchema>;

export const activationSchema = z.object({
  id: idSchema,
  user_id: idSchema,
  user_code: z.string().optional(),
  package_id: idSchema,
  package_name: z.string(),
  amount_usd: moneySchema,
  amount_lds: moneySchema,
  /** 'self' when paid from the activation wallet, 'admin' when granted. */
  source: z.enum(['self', 'admin', 'blockchain']),
  tx_hash: z.string().nullable(),
  activated_at: isoDateTimeSchema,
});
export type Activation = z.infer<typeof activationSchema>;

/* ------------------------------------------------------------------ *
 * Income — the six Alpha One types (§8) + achievement rewards
 * ------------------------------------------------------------------ */

export const incomeTypeSchema = z.enum([
  'direct_referral',
  'level_income',
  'alpha_club_a',
  'alpha_club_b',
  'elite_rank',
  'achievement_reward',
]);
export type IncomeType = z.infer<typeof incomeTypeSchema>;

export const INCOME_TYPES = incomeTypeSchema.options;

export const INCOME_TYPE_LABELS: Record<IncomeType, string> = {
  direct_referral: 'Direct Referral Bonus',
  level_income: 'Level Income',
  alpha_club_a: 'Alpha Club A',
  alpha_club_b: 'Alpha Club B',
  elite_rank: 'Elite Rank Benefits',
  achievement_reward: 'Achievement Rewards',
};

/**
 * A single ledger row. `dedup_key` mirrors the server-side STORED generated
 * column guarded by a UNIQUE index (§9) — the DB, not the app, guarantees
 * at-most-one payout per key. It is null for non-idempotent rows.
 */
export const incomeRecordSchema = z.object({
  id: idSchema,
  user_id: idSchema,
  user_code: z.string().optional(),
  type: incomeTypeSchema,
  amount_usd: moneySchema,
  amount_lds: moneySchema,
  /** The downline member whose action triggered this payout, if any. */
  from_user_id: idSchema.nullable(),
  from_user_code: z.string().nullable(),
  level: z.number().nullable(),
  description: z.string().nullable(),
  dedup_key: z.string().nullable(),
  created_at: isoDateTimeSchema,
});
export type IncomeRecord = z.infer<typeof incomeRecordSchema>;

/** Direct Referral Bonus — 50% for directs 1 & 2, 30% from direct 3 on. */
export const directReferralIncomeSchema = incomeRecordSchema.extend({
  type: z.literal('direct_referral'),
  direct_position: z.number(),
  percentage: moneySchema,
});
export type DirectReferralIncome = z.infer<typeof directReferralIncomeSchema>;

/** Level Income — 7 levels, $6/$5/$4/$2/$1/$1/$1 in LDS. */
export const levelIncomeSchema = incomeRecordSchema.extend({
  type: z.literal('level_income'),
  level: z.number().min(1).max(7),
});
export type LevelIncome = z.infer<typeof levelIncomeSchema>;

export const alphaClubTierSchema = z.object({
  tier: z.number().min(1).max(7),
  rank_code: z.string(),
  requirement_label: z.string(),
  required_count: z.number(),
  achieved_count: z.number(),
  gift_usd: moneySchema,
  gift_lds: moneySchema,
  achieved: z.boolean(),
  achieved_at: isoDateTimeSchema.nullable(),
});
export type AlphaClubTier = z.infer<typeof alphaClubTierSchema>;

/**
 * Admin-only: one row per member making SOME progress toward a Club B tier
 * (qualifying_count > 0), sorted closest-to-qualifying first. Club B's own
 * payout table is legitimately empty right now (see AlphaClubB.php) — this
 * gives admins visibility into who's nearest and by how much, per the client's
 * own request ("show details which id is nearest and by how much... remaining").
 */
export const clubBProgressRowSchema = z.object({
  user_code: z.string(),
  name: z.string(),
  qualifying_count: z.number(),
  next_tier_level: z.number().nullable(),
  next_tier_required: z.number().nullable(),
  remaining: z.number(),
});
export type ClubBProgressRow = z.infer<typeof clubBProgressRowSchema>;

/** Alpha Club A — 7 tiers, ALPHA 1..7, $50 → $1,500 LDS. */
export const alphaClubAIncomeSchema = incomeRecordSchema.extend({
  type: z.literal('alpha_club_a'),
  tier: z.number().min(1).max(7),
  rank_code: z.string(),
});
export type AlphaClubAIncome = z.infer<typeof alphaClubAIncomeSchema>;

/** Alpha Club B — 7 tiers, B1..B7, $1,000 → $30,000 LDS. */
export const alphaClubBIncomeSchema = incomeRecordSchema.extend({
  type: z.literal('alpha_club_b'),
  tier: z.number().min(1).max(7),
  rank_code: z.string(),
});
export type AlphaClubBIncome = z.infer<typeof alphaClubBIncomeSchema>;

export const eliteRankTierSchema = z.enum(['silver', 'gold', 'diamond']);
export type EliteRankTier = z.infer<typeof eliteRankTierSchema>;

/**
 * Elite Rank Benefits — recurring monthly payout with a term counter.
 * Silver (B5) $50×10mo · Gold (B6) $100×15mo · Diamond (B7) $300×20mo + KIA EV9.
 */
export const eliteRankIncomeSchema = incomeRecordSchema.extend({
  type: z.literal('elite_rank'),
  tier: eliteRankTierSchema,
  installment_no: z.number(),
  total_installments: z.number(),
});
export type EliteRankIncome = z.infer<typeof eliteRankIncomeSchema>;

export const eliteRankEnrollmentSchema = z.object({
  id: idSchema,
  user_id: idSchema,
  tier: eliteRankTierSchema,
  monthly_amount_usd: moneySchema,
  monthly_amount_lds: moneySchema,
  total_installments: z.number(),
  paid_installments: z.number(),
  next_payout_at: isoDateTimeSchema.nullable(),
  status: z.enum(['active', 'paused', 'completed', 'forfeited']),
  started_at: isoDateTimeSchema,
});
export type EliteRankEnrollment = z.infer<typeof eliteRankEnrollmentSchema>;

/** Achievement Rewards — binary, unlocked when BOTH legs hit the milestone. */
export const achievementRewardSchema = z.object({
  id: idSchema,
  milestone: z.number(),
  required_left: z.number(),
  required_right: z.number(),
  current_left: z.number(),
  current_right: z.number(),
  reward_usd: moneySchema,
  reward_lds: moneySchema,
  achieved: z.boolean(),
  achieved_at: isoDateTimeSchema.nullable(),
});
export type AchievementReward = z.infer<typeof achievementRewardSchema>;

/**
 * Admin-only: the SAME Achievement Reward tracker a member sees in their own
 * panel (achievementRewardSchema, above — `rewards` here is that exact array),
 * plus the Active/Inactive leg breakdown the member-facing view doesn't
 * expose. Looked up by user_code (or numeric id) via
 * GET /admin/members/{id}/reward-tracker — see
 * MemberController::rewardTracker() / BinaryCounterService::countLegBreakdown().
 *
 * Deliberately has no "business volume" field: binary_tree.left_business_usd /
 * right_business_usd are never computed anywhere in this codebase, so there is
 * no honest number to show.
 */
export const memberRewardTrackerSchema = z.object({
  user_id: idSchema,
  user_code: z.string(),
  name: z.string(),
  left_active: z.number(),
  left_inactive: z.number(),
  left_total: z.number(),
  right_active: z.number(),
  right_inactive: z.number(),
  right_total: z.number(),
  matched: z.number(),
  rewards: z.array(achievementRewardSchema),
  /** For the first unachieved tier: how many more matched (min of left, right) are needed. 0 if every tier is achieved. */
  next_reward_remaining: z.number(),
  /** Alpha Club A tier progress for the same member — identical shape to the member's own alphaClubAProgress() view. */
  alpha_club_a: z.array(alphaClubTierSchema),
  /** For the first unachieved Club A tier: how many more qualifying downline members are needed. 0 if every tier is achieved. */
  alpha_club_a_remaining: z.number(),
});
export type MemberRewardTracker = z.infer<typeof memberRewardTrackerSchema>;

const incomeTotalsShape = {
  direct_referral: moneySchema.optional(),
  level_income: moneySchema.optional(),
  alpha_club_a: moneySchema.optional(),
  alpha_club_b: moneySchema.optional(),
  elite_rank: moneySchema.optional(),
  achievement_reward: moneySchema.optional(),
};

export const incomeSummarySchema = z.object({
  totals: z.object(incomeTotalsShape),
  /** Same breakdown, in LDS — the plan pays out in LDS, prefer this for display. */
  totals_lds: z.object(incomeTotalsShape),
  total_usd: moneySchema,
  total_lds: moneySchema,
  today_usd: moneySchema,
});
export type IncomeSummary = z.infer<typeof incomeSummarySchema>;

/* ------------------------------------------------------------------ *
 * Team / trees
 * ------------------------------------------------------------------ */

/**
 * Binary placement node — a tree SEPARATE from the unilevel sponsor tree.
 * Achievement Rewards run on this; referral/level/club income run on sponsor_id.
 */
// Input type is `unknown` because the wire payload uses loose id types that
// idSchema normalises to strings — only the OUTPUT must match BinaryTreeNode.
export const binaryTreeNodeSchema: z.ZodType<BinaryTreeNode, z.ZodTypeDef, unknown> = z.lazy(() =>
  z.object({
    id: idSchema,
    user_id: idSchema,
    user_code: z.string(),
    name: z.string(),
    position: z.enum(['left', 'right']).nullable(),
    parent_id: idSchema.nullable(),
    is_activated: z.boolean(),
    left_count: z.number(),
    right_count: z.number(),
    /** Join date — same source as userSchema.created_at. */
    created_at: isoDateTimeSchema.nullable(),
    /** Package-activation date — null when the member hasn't activated yet. */
    activated_at: isoDateTimeSchema.nullable(),
    left: binaryTreeNodeSchema.nullable(),
    right: binaryTreeNodeSchema.nullable(),
  }),
);

export interface BinaryTreeNode {
  id: string;
  user_id: string;
  user_code: string;
  name: string;
  position: 'left' | 'right' | null;
  parent_id: string | null;
  is_activated: boolean;
  left_count: number;
  right_count: number;
  created_at: string | null;
  activated_at: string | null;
  left: BinaryTreeNode | null;
  right: BinaryTreeNode | null;
}

export const teamStatsSchema = z.object({
  direct_count: z.number(),
  direct_active: z.number(),
  total_team: z.number(),
  total_active: z.number(),
  left_count: z.number(),
  right_count: z.number(),
  // Admin-granted virtual power units, own-account only (this endpoint never
  // returns another member's stats) — shown as its own box so it's never
  // confused with real recruits, and never visible to anyone but this member.
  virtual_power_units: z.number(),
  levels: z.array(z.object({ level: z.number(), total: z.number(), active: z.number() })),
});
export type TeamStats = z.infer<typeof teamStatsSchema>;

/* ------------------------------------------------------------------ *
 * Wallet / money movement
 * ------------------------------------------------------------------ */

export const walletSchema = z.object({
  user_id: idSchema,
  /** Funds usable for package activation (gold ★ pill in the topbar). */
  activation_balance: moneySchema,
  /** Earned income, withdrawable (green pill). Paid out in LDS — show the _lds twin, not this. */
  income_balance: moneySchema,
  income_balance_lds: moneySchema,
  total_earned: moneySchema,
  total_earned_lds: moneySchema,
  total_withdrawn: moneySchema,
  total_withdrawn_lds: moneySchema,
  updated_at: isoDateTimeSchema,
});
export type Wallet = z.infer<typeof walletSchema>;

export const walletKindSchema = z.enum(['activation', 'income']);
export type WalletKind = z.infer<typeof walletKindSchema>;

export const transactionSchema = z.object({
  id: idSchema,
  user_id: idSchema,
  wallet: walletKindSchema,
  direction: z.enum(['credit', 'debit']),
  amount_usd: moneySchema,
  amount_lds: moneySchema,
  balance_after: moneySchema,
  reference_type: z.string().nullable(),
  reference_id: idSchema.nullable(),
  description: z.string().nullable(),
  created_at: isoDateTimeSchema,
});
export type Transaction = z.infer<typeof transactionSchema>;

/**
 * A past admin-initiated wallet adjustment (`/admin/wallet/add` or `/deduct`),
 * as shown in the WalletAdjust history list. Built server-side from the audit
 * log, not wallet_transactions — see Present::walletAdjustment() for why.
 */
export const walletAdjustmentSchema = z.object({
  id: idSchema,
  user_id: idSchema.nullable(),
  user_code: z.string().nullable(),
  user_name: z.string().nullable(),
  wallet: walletKindSchema.nullable(),
  direction: z.enum(['credit', 'debit']),
  amount_usd: moneySchema,
  reason: z.string().nullable(),
  admin_name: z.string().nullable(),
  admin_code: z.string().nullable(),
  created_at: isoDateTimeSchema,
});
export type WalletAdjustment = z.infer<typeof walletAdjustmentSchema>;

export const depositSchema = z.object({
  id: idSchema,
  user_id: idSchema,
  user_code: z.string().optional(),
  amount_usd: moneySchema,
  amount_lds: moneySchema,
  method: z.enum(['manual', 'gateway', 'blockchain']),
  tx_hash: z.string().nullable(),
  chain: z.string().nullable(),
  from_address: z.string().nullable(),
  proof_url: z.string().nullable(),
  status: approvalStatusSchema,
  admin_note: z.string().nullable(),
  created_at: isoDateTimeSchema,
  processed_at: isoDateTimeSchema.nullable(),
});
export type Deposit = z.infer<typeof depositSchema>;

/**
 * Alpha One withdrawal terms (§8): 0% admin fee, $25 minimum, 24×7 auto.
 * `fee_usd` stays on the type because the API returns it, but it is 0.
 */
export const withdrawalSchema = z.object({
  id: idSchema,
  user_id: idSchema,
  user_code: z.string().optional(),
  amount_usd: moneySchema,
  amount_lds: moneySchema,
  fee_usd: moneySchema,
  net_usd: moneySchema,
  wallet_address: z.string(),
  chain: z.string().nullable(),
  tx_hash: z.string().nullable(),
  // 'failed' (2026-08-02, auto-withdrawal) is DELIBERATELY distinct from
  // 'rejected' — 'rejected' means the ledger debit was refunded
  // (ModerationController::rejectWithdrawal()); 'failed' means an automatic
  // on-chain send attempt errored (AutoPayoutService::markFailed()) and the
  // debit is still outstanding, awaiting a human decision to retry or
  // refund. Collapsing these into the same wire value would tell a member
  // their money came back when it didn't.
  status: approvalStatusSchema.or(z.enum(['processing', 'paid', 'failed'])),
  admin_note: z.string().nullable(),
  created_at: isoDateTimeSchema,
  processed_at: isoDateTimeSchema.nullable(),
});
export type Withdrawal = z.infer<typeof withdrawalSchema>;

export const WITHDRAWAL_MIN_USD = 25;
export const WITHDRAWAL_FEE_PERCENT = 0;
/**
 * Client requirement, 2026-08-01 (Hindi original: "मिनिमम टोकन withdraw 750
 * TOKEN"): the withdrawal minimum is a FLAT 750 LDS, not a USD figure
 * translated into LDS. This is what the withdrawal REQUEST/quote flow uses;
 * WITHDRAWAL_MIN_USD above is kept only because historical withdrawal
 * records still carry amount_usd for display elsewhere (admin reports, the
 * member's own history), not because the withdrawal form uses it anymore.
 *
 * 2026-09-11, LDS1 subdomain: build-time configurable (VITE_WITHDRAWAL_MIN,
 * defaults to 750 — main site unchanged) because on that deployment this
 * same field means USDT 1:1 with USD, not LDS at the $0.04 rate — 750 would
 * mean a $750 minimum withdrawal, clearly not intended. Must match the
 * backend's own `withdrawal_min_lds` setting for that deployment or the
 * quoted minimum and the form's enforced minimum would disagree.
 */
export const WITHDRAWAL_MIN_LDS = Number(
  (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, unknown> }).env?.VITE_WITHDRAWAL_MIN) || 750,
);

/* ------------------------------------------------------------------ *
 * Rank
 * ------------------------------------------------------------------ */

export const rankSchema = z.object({
  id: idSchema,
  code: z.string(),
  name: z.string(),
  /** Which programme the rank belongs to. */
  family: z.enum(['alpha_a', 'alpha_b', 'elite']),
  tier: z.number(),
  requirement_label: z.string(),
  reward_usd: moneySchema,
  reward_lds: moneySchema,
  achieved: z.boolean(),
  achieved_at: isoDateTimeSchema.nullable(),
  progress_current: z.number(),
  progress_required: z.number(),
});
export type Rank = z.infer<typeof rankSchema>;

/* ------------------------------------------------------------------ *
 * Support
 * ------------------------------------------------------------------ */

export const ticketStatusSchema = z.enum(['open', 'answered', 'closed']);
export type TicketStatus = z.infer<typeof ticketStatusSchema>;

export const ticketReplySchema = z.object({
  id: idSchema,
  ticket_id: idSchema,
  author_type: z.enum(['user', 'admin']),
  author_name: z.string(),
  message: z.string(),
  created_at: isoDateTimeSchema,
});
export type TicketReply = z.infer<typeof ticketReplySchema>;

export const ticketSchema = z.object({
  id: idSchema,
  user_id: idSchema,
  user_code: z.string().optional(),
  subject: z.string(),
  category: z.string().nullable(),
  priority: z.enum(['low', 'normal', 'high']),
  status: ticketStatusSchema,
  message: z.string(),
  replies: z.array(ticketReplySchema).optional(),
  created_at: isoDateTimeSchema,
  updated_at: isoDateTimeSchema,
});
export type Ticket = z.infer<typeof ticketSchema>;

/* ------------------------------------------------------------------ *
 * System / admin
 * ------------------------------------------------------------------ */

export const newsItemSchema = z.object({
  id: idSchema,
  title: z.string(),
  body: z.string(),
  is_published: z.boolean(),
  /** Optional banner/offer image — drives the member dashboard news popup. */
  image_url: z.string().nullable().optional(),
  created_at: isoDateTimeSchema,
});
export type NewsItem = z.infer<typeof newsItemSchema>;

export const downloadItemSchema = z.object({
  id: idSchema,
  title: z.string(),
  description: z.string().nullable(),
  file_url: z.string(),
  category: z.string().nullable(),
  created_at: isoDateTimeSchema,
});
export type DownloadItem = z.infer<typeof downloadItemSchema>;

export const cronLogSchema = z.object({
  id: idSchema,
  job: z.string(),
  status: z.enum(['running', 'success', 'failed']),
  message: z.string().nullable(),
  ran_for: z.string().nullable(),
  duration_ms: z.number().nullable(),
  created_at: isoDateTimeSchema,
});
export type CronLog = z.infer<typeof cronLogSchema>;

export const settingSchema = z.object({
  key: z.string(),
  value: z.string().nullable(),
  group: z.string().nullable(),
});
export type Setting = z.infer<typeof settingSchema>;

export const kycRequestSchema = z.object({
  id: idSchema,
  user_id: idSchema,
  user_code: z.string(),
  name: z.string(),
  document_type: z.string(),
  document_number: z.string(),
  front_url: z.string().nullable(),
  back_url: z.string().nullable(),
  status: kycStatusSchema,
  admin_note: z.string().nullable(),
  created_at: isoDateTimeSchema,
});
export type KycRequest = z.infer<typeof kycRequestSchema>;

export const adminDashboardSchema = z.object({
  members_total: z.number(),
  members_active: z.number(),
  members_inactive: z.number(),
  members_blocked: z.number(),
  deposits_today_usd: moneySchema,
  deposits_total_usd: moneySchema,
  withdrawals_pending: z.number(),
  withdrawals_pending_usd: moneySchema,
  income_paid_usd: moneySchema,
  activations_today: z.number(),
  open_tickets: z.number(),
  pending_kyc: z.number(),
  chart_daily: z.array(
    z.object({ date: z.string(), deposits: z.number(), withdrawals: z.number(), income: z.number() }),
  ),
});
export type AdminDashboard = z.infer<typeof adminDashboardSchema>;

/* ------------------------------------------------------------------ *
 * Web3 wallet auth (DApp registration/login) — 2026-07-30 replacement for
 * email+password. See packages/api-client/src/endpoints/walletAuth.ts.
 * ------------------------------------------------------------------ */

export const dappRequestStatusSchema = z.enum(['pending', 'confirmed', 'rejected']);
export type DappRequestStatus = z.infer<typeof dappRequestStatusSchema>;

/** One DApp registration attempt — shown on both the admin and "My Wallet" screens. */
export const dappRegistrationRequestSchema = z.object({
  id: idSchema,
  wallet_address: z.string(),
  status: dappRequestStatusSchema,
  tx_hash: z.string().nullable(),
  chain_id: z.number().nullable(),
  amount_lds: moneySchema.nullable(),
  reject_reason: z.string().nullable(),
  attempts: z.number(),
  created_at: isoDateTimeSchema,
  verified_at: isoDateTimeSchema.nullable(),
});
export type DappRegistrationRequest = z.infer<typeof dappRegistrationRequestSchema>;

/** Admin list row — a superset of dappRegistrationRequestSchema with reviewer/member context. */
export const adminDappRequestSchema = dappRegistrationRequestSchema.extend({
  sponsor_code: z.string().nullable(),
  user_id: idSchema.nullable(),
  user_code: z.string().nullable(),
  user_name: z.string().nullable(),
  from_address: z.string().nullable(),
  to_address: z.string().nullable(),
  reviewed_by_admin_name: z.string().nullable(),
  last_checked_at: isoDateTimeSchema.nullable(),
});
export type AdminDappRequest = z.infer<typeof adminDappRequestSchema>;

export const userDashboardSchema = z.object({
  wallet: walletSchema,
  income: incomeSummarySchema,
  team: teamStatsSchema,
  rank_code: z.string().nullable(),
  next_rank: rankSchema.nullable(),
  is_activated: z.boolean(),
  referral_link: z.string(),
  chart_daily: z.array(z.object({ date: z.string(), amount: z.number() })),
});
export type UserDashboard = z.infer<typeof userDashboardSchema>;

/* ------------------------------------------------------------------ *
 * Virtual Power — admin-granted binary leg units (client add-on)
 * ------------------------------------------------------------------ */

/**
 * A single admin grant of virtual left/right leg units on the binary tree.
 * Behaves like a real active downline member for both matching/achievement
 * income and every ancestor's leg count — see BinaryCounterService and
 * VirtualPowerAggregator server-side for how the cascade works.
 */
export const virtualPowerGrantSchema = z.object({
  id: idSchema,
  user_id: idSchema,
  user_code: z.string().nullable().optional(),
  user_name: z.string().nullable().optional(),
  position: z.enum(['left', 'right']),
  units: z.number(),
  note: z.string().nullable(),
  admin_name: z.string().nullable().optional(),
  admin_code: z.string().nullable().optional(),
  created_at: isoDateTimeSchema,
});
export type VirtualPowerGrant = z.infer<typeof virtualPowerGrantSchema>;
